import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MediaAsset } from '@ghostscreen/contracts';
import { getMediaPreviewPlayUrl } from '../../content/mediaPreview';
import { MediaPreviewLightbox } from '../../content/MediaPreviewLightbox';
import { MediaThumb } from '../../content/MediaThumb';

type PlayContentMediaGridProps = {
  assets: MediaAsset[];
  selectedId: string | null;
  disabled?: boolean;
  /** 2 fits the Preview column; 3 for a wider surface. */
  columns?: 2 | 3;
  /** Fixed filmstrip thumbs for Manage — not full-width aspect tiles. */
  compact?: boolean;
  onSelect: (assetId: string) => void;
};

/**
 * Browse-sized media grid for play-on-display — not the compact playlist filmstrip.
 */
export function PlayContentMediaGrid({
  assets,
  selectedId,
  disabled = false,
  columns = 2,
  compact = false,
  onSelect,
}: PlayContentMediaGridProps) {
  const { t } = useTranslation('displays');
  const [preview, setPreview] = useState<MediaAsset | null>(null);

  if (assets.length === 0) {
    return <p className="m-0 text-sm text-muted">{t('playContent.empty')}</p>;
  }

  if (compact) {
    return (
      <>
        <div className="flex flex-wrap gap-1.5" role="listbox" aria-label={t('playContent.readyMedia')}>
          {assets.map((asset) => {
            const isSelected = selectedId === asset.id;
            const canPreview = Boolean(getMediaPreviewPlayUrl(asset));
            return (
              <div
                key={asset.id}
                role="option"
                aria-selected={isSelected}
                className={[
                  'group relative h-12 w-[4.75rem] shrink-0 overflow-hidden rounded-[8px] border transition',
                  isSelected
                    ? 'border-signal ring-1 ring-[color-mix(in_srgb,var(--gs-signal)_32%,transparent)]'
                    : 'border-line hover:border-[var(--gs-accent)]',
                ].join(' ')}
                style={{ background: 'var(--gs-panel)' }}
              >
                <button
                  type="button"
                  disabled={disabled}
                  title={asset.originalFilename}
                  aria-label={asset.originalFilename}
                  onClick={() => onSelect(asset.id)}
                  className="block h-full w-full disabled:opacity-50"
                >
                  <MediaThumb asset={asset} className="absolute inset-0 h-full w-full" />
                </button>
                {canPreview ? (
                  <button
                    type="button"
                    disabled={disabled}
                    aria-label={t('playContent.previewAria', { name: asset.originalFilename })}
                    title={t('playContent.preview')}
                    onClick={() => setPreview(asset)}
                    className="absolute end-0.5 top-0.5 grid h-5 w-5 place-items-center rounded-md bg-black/55 text-white opacity-0 transition group-hover:opacity-100 focus:opacity-100 disabled:opacity-40"
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>

        {preview ? (
          <MediaPreviewLightbox asset={preview} onClose={() => setPreview(null)} />
        ) : null}
      </>
    );
  }

  const gridClass =
    columns === 3 ? 'grid grid-cols-2 gap-3 sm:grid-cols-3' : 'grid grid-cols-2 gap-2.5';

  return (
    <>
      <div className={gridClass} role="listbox" aria-label={t('playContent.readyMedia')}>
        {assets.map((asset) => {
          const isSelected = selectedId === asset.id;
          const canPreview = Boolean(getMediaPreviewPlayUrl(asset));
          return (
            <div
              key={asset.id}
              role="option"
              aria-selected={isSelected}
              className={[
                'group relative overflow-hidden rounded-[12px] border transition',
                isSelected
                  ? 'border-signal ring-2 ring-[color-mix(in_srgb,var(--gs-signal)_32%,transparent)]'
                  : 'border-line hover:border-[var(--gs-accent)]',
              ].join(' ')}
              style={{ background: 'var(--gs-panel)' }}
            >
              <button
                type="button"
                disabled={disabled}
                title={asset.originalFilename}
                onClick={() => onSelect(asset.id)}
                className="block w-full text-start disabled:opacity-50"
              >
                <MediaThumb asset={asset} className="aspect-video w-full" />
                <span className="block truncate px-2.5 py-2 text-xs font-semibold leading-tight text-ink">
                  {asset.originalFilename}
                </span>
              </button>
              {canPreview ? (
                <button
                  type="button"
                  disabled={disabled}
                  aria-label={t('playContent.previewAria', { name: asset.originalFilename })}
                  title={t('playContent.preview')}
                  onClick={() => setPreview(asset)}
                  className="absolute end-2 top-2 grid h-7 w-7 place-items-center rounded-md bg-black/55 text-white opacity-0 transition group-hover:opacity-100 focus:opacity-100 disabled:opacity-40"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      {preview ? (
        <MediaPreviewLightbox asset={preview} onClose={() => setPreview(null)} />
      ) : null}
    </>
  );
}
